import { NetworkRouter, type NetworkRouterCard } from "@/components/NetworkRouter";
import { withNetworkUtm } from "@/lib/network";

// Registry of Shrink Network destinations, keyed for context-aware routing.
// Each entry pairs the spec's verb label with the destination property so a
// "next step" reads as "[Verb] at [Property]" (e.g. "Work with the pattern at
// shrinQ"). External hrefs keep their trailing slashes as the network sites
// expose them.
export type NextStepKey =
  | "shrinkopedia"
  | "shrinktionary"
  | "anxietyresource"
  | "depressionresource"
  | "anxietyresearch"
  | "psychiatryrx"
  | "shrinkdaily"
  | "shrinq"
  | "unstuck"
  | "quitscrolling"
  | "shrinkiatry"
  | "shrinkmd";

// Each destination deep links to that site's "start here" style hub rather
// than its homepage, so a reader lands on a page that already answers "where
// do I begin". Every URL below is listed in that site's own sitemap (checked
// 2026-09-24). shrinkMD publishes its URLs without a trailing slash.
const DESTINATIONS: Record<NextStepKey, { verb: string; property: string; href: string }> = {
  shrinkopedia: { verb: "Understand the concept", property: "Shrinkopedia", href: "https://shrinkopedia.com/where-to-start/" },
  shrinktionary: { verb: "Understand the language", property: "Shrinktionary", href: "https://shrinktionary.com/terms/" },
  anxietyresource: { verb: "Understand anxiety", property: "AnxietyResource", href: "https://anxietyresource.org/start-here/" },
  depressionresource: { verb: "Understand depression", property: "DepressionResource", href: "https://depressionresource.org/start-here/" },
  anxietyresearch: { verb: "See the evidence", property: "AnxietyResearch", href: "https://anxietyresearch.org/evidence-summaries/" },
  psychiatryrx: { verb: "Understand the medication", property: "PsychiatryRx", href: "https://psychiatryrx.org/medications/" },
  shrinkdaily: { verb: "Learn a little each day", property: "ShrinkDaily", href: "https://shrinkdaily.com/" },
  shrinq: { verb: "Work with the pattern", property: "shrinQ", href: "https://shrinq.com/program/" },
  unstuck: { verb: "Reset in the moment", property: "Unstuck", href: "https://beunstuck.app/reset-method/" },
  quitscrolling: { verb: "Protect your attention", property: "QuitScrolling", href: "https://quitscrolling.app/" },
  shrinkiatry: { verb: "Learn the profession", property: "shrinkiatry", href: "https://shrinkiatry.com/psychiatry-operating-room/" },
  shrinkmd: { verb: "Get clinical care", property: "shrinkMD", href: "https://shrinkmd.com/how-it-works" },
};

/** A page-specific deep link for one destination, with its own card label. */
export interface NextStepDeepLink {
  href: string;
  label: string;
}

const SHRINK_NETWORK_URL = "https://shrinknetwork.com/";

interface NetworkNextStepProps {
  /** Context-aware destinations for this page, capped at three. */
  destinations: NextStepKey[];
  /**
   * Campaign slug for UTM attribution. Should describe the source page or
   * topic (e.g. "about_next_step", "book_havoc_next_step"). Defaults to the
   * generic founder's-office slug when omitted.
   */
  campaign?: string;
  /**
   * Topic deep links that replace a destination's hub link on this page,
   * e.g. { shrinkopedia: { href: ".../library/rumination/", label: "Read about rumination at Shrinkopedia" } }.
   */
  deepLinks?: Partial<Record<NextStepKey, NextStepDeepLink>>;
  className?: string;
}

// Shared "Your next step in The Shrink Network" block for content pages. It
// answers where the reader is, why the network exists, and where to go next,
// reusing the NetworkRouter card UI for the topic-matched destinations. Every
// outbound link is UTM-tagged so cross-network journeys are measurable.
export function NetworkNextStep({
  destinations,
  campaign = "founder_office_next_step",
  deepLinks,
  className,
}: NetworkNextStepProps) {
  const cards: NetworkRouterCard[] = destinations.slice(0, 3).map((key) => {
    const d = DESTINATIONS[key];
    const deep = deepLinks?.[key];
    return {
      label: deep?.label ?? `${d.verb} at ${d.property}`,
      href: withNetworkUtm(deep?.href ?? d.href, campaign),
    };
  });

  return (
    <div
      role="region"
      aria-label="Your next step in The Shrink Network"
      className={`network-next-step ${className ?? ""}`}
    >
      <span className="text-xs font-bold tracking-[0.25em] uppercase text-accent">
        Your next step in The Shrink Network
      </span>
      <p className="mt-4 font-serif text-lg md:text-xl text-primary leading-snug">
        <strong className="font-medium">You are here: Shariq Refai</strong>, the
        founder's office of The Shrink Network.
      </p>
      <p className="mt-3 max-w-2xl text-secondary font-light leading-relaxed">
        Each site in the network has one job. No matter where you enter, we help
        you find the next step that makes sense.
      </p>
      <div className="mt-6">
        <a
          href={withNetworkUtm(SHRINK_NETWORK_URL, campaign)}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex h-12 items-center justify-center bg-primary text-primary-foreground px-8 text-sm font-medium hover:bg-secondary transition-colors"
        >
          Explore The Shrink Network
        </a>
      </div>
      <div className="mt-10">
        <NetworkRouter
          eyebrow="Want to understand more first?"
          columns={3}
          cards={cards}
        />
      </div>
    </div>
  );
}
